/**
 * @citeclerk/goodlaw — the ONE shared source of good-law PRESENTATION + cite-gate POLICY for CiteClerk.
 *
 * Why this exists: the good-law DETERMINATION (does a case have negative treatment, the honest status) lives
 * in the shared engine (packages/engine, `honest_status`). But the JS layer that consumes that status —
 * "is this status bad-law / not citeable?", "is a green clearance earned?", "what tier/label is this status?"
 * — was DUPLICATED in Steel and Draft and drifted (Draft once shipped a stale copy; Steel has 3 copies of
 * VERIFIED_SOURCES, one already stale). This package is that logic, extracted once. Both surfaces import it →
 * a future good-law JS change lands here → both get it.
 *
 * PURE / ISOMORPHIC: no I/O, no DB, no app state, no server-only (node) or browser-only deps. Usable
 * identically from Steel's server + client and Draft's server + client (same rule as @citeclerk/ui).
 */

// The canonical HONEST status vocabulary the engine emits (packages/engine honest_status). 'clear' is a
// Steel-only verified-good value (from a curated flag); the engine's honest path never emits it.
export const STATUS = Object.freeze({
  NEGATIVE: 'negative',
  CAUTION: 'caution',
  NO_KNOWN_NEGATIVE: 'no-known-negative',
  PENDING: 'pending',
  // ⭐ ENGINE-3 — 'unavailable' = THE CHECK COULD NOT RUN (today: the daily spend breaker tripped).
  // It is deliberately NOT 'pending'. 'pending' is the innocent state of a case nobody has confirmed
  // yet, and a check that failed for budget reasons wearing that word is indistinguishable from one
  // that simply has not run. Measured before this change: a spend-capped engine response resolved
  // all the way to 'no-known-negative' — the GREEN state — because the payload's all-false flags read
  // as a completed answer. A clearance nobody earned is the one outcome the moat cannot survive.
  UNAVAILABLE: 'unavailable',
});

// ─── CITE-GATE / BAD-LAW PREDICATE (⚠ MOAT-CRITICAL) ───
// "Is this status no-longer-good-law → NEVER citeable as affirmative support?" Robust to BOTH the honest
// vocab ('negative') and the legacy engine vocab ('not_good'). This is THE predicate whose drift = citing bad
// law in a brief; it must be identical on both surfaces. Steel inlined `status === 'negative'`; Draft had a
// local copy — both now import this.
export function isBadLaw(status) {
  const v = String(status || '').toLowerCase();
  return v === 'negative' || v === 'not_good' || v === 'bad' || v === 'overruled';
}

// ─── VERIFIED-SOURCE POLICY ───
// A green "Good law" clearance is earned ONLY when the determination came from a VERIFIED source (a curated
// flag or a completed ensemble). A cheap/absence check never earns green — that is the moat, made visible.
export const VERIFIED_SOURCES = Object.freeze(new Set([
  'curated', 'engine_ensemble', 'ensemble', 'ondemand-v1', 'enumerated-v1', 'tagonview-v1',
]));
export function isVerifiedSource(source) {
  return VERIFIED_SOURCES.has(source);
}

// ─── CANONICAL STATUS → TIER + LABEL ───
// The semantic classification both surfaces' chip functions share. Each surface maps `.tier` to its OWN CSS
// class + output shape (Steel `{dot,cls,tip}`, Draft `{cls,glyph}`) and MAY override `.label` (Steel shows a
// treatment-specific label for negative). Tiers: 'negative' | 'caution' | 'no-known-negative' | 'verified-good'
// | 'checking' | 'pending'. The MOAT rule lives here: a non-verified 'clear' is NEVER 'verified-good' — it is
// the neutral 'no-known-negative'; anything unrecognized is 'pending' (never a clearance from silence).
export function goodLawTier(status, { source } = {}) {
  const v = String(status || 'pending').toLowerCase();
  if (v === 'na' || v === 'n/a') return 'na'; // NON-MERITS (cert denial / order) — good-law does not apply
  if (isBadLaw(v)) return 'negative';
  if (v === 'caution' || v === 'questioned' || v === 'criticized') return 'caution';
  if (v === 'no-known-negative') return 'no-known-negative';
  // SESSION D (Steel read-path overhaul) — two new server states, both additive:
  // 'unconfirmed' = gate-suppressed treatment signal (advisory edges) shown honestly as "possible
  // negative treatment — unconfirmed" — its OWN tier (neither a caution claim nor a clearance).
  // 'no-negative-detected' = the checked-denominator absence state ("checked {n} citing opinions
  // through {date}") — folds to the neutral no-known-negative tier (same moat semantics: an honest
  // non-claim, never green).
  if (v === 'unconfirmed') return 'unconfirmed';
  if (v === 'no-negative-detected') return 'no-known-negative';
  if (v === 'clear') return isVerifiedSource(source) ? 'verified-good' : 'no-known-negative';
  if (v === 'checking') return 'checking';
  // ENGINE-3: the check could not run. Its own tier so the UI can say so out loud, and so it can
  // never be silently folded into either a clearance or the innocent 'pending'.
  if (v === 'unavailable') return 'unavailable';
  return 'pending'; // 'pending' / 'unknown' / legacy 'good' / null — honest, never a green clearance
}

// The canonical label per tier (a surface may override, e.g. Steel's treatment-specific negative label).
export const TIER_LABEL = Object.freeze({
  negative: 'Not good law',
  caution: 'Caution',
  'no-known-negative': 'No known negative treatment',
  'verified-good': 'Good law',
  checking: 'Checking…',
  pending: 'Good-law status pending',
  // Says WHY there is no answer. "Verification unavailable" is a report about the instrument;
  // "pending" is a report about the case, and conflating them is what let a budget failure read as
  // a clean bill of health.
  unavailable: 'Verification unavailable',
  na: 'N/A — not a merits opinion', // honest: a non-merits disposition has NO good-law status
  unconfirmed: 'Possible negative treatment — unconfirmed', // SESSION D gate dial: tiered, never dropped
});

// ─── ⭐ SESSION G+E A4 — THE ONE TREATMENT VOCABULARY (⚠ MOAT-ADJACENT) ───
//
// THE DEFECT THIS EXISTS TO KILL. On one screen, for one fact, the product said two different words:
// the citator card read "Abrogated by Kennedy v. Bremerton School Dist." while the composed answer
// three inches above it read "It was overruled by Kennedy v. Bremerton Sch. Dist.." A reader cannot
// tell whether that is two findings or one, and a product whose entire claim is that it tells you the
// truth about a case's standing cannot afford the ambiguity. The cause was structural, not a typo:
// the citator's labels lived in Steel's CLIENT (lib/goodlawDisplay.js) and the answer path's prose
// lived in Steel's SERVER (routes/answerGen.js), which hard-coded the string "overruled by" for
// EVERY negative treatment class — abrogations, supersessions and reversals all came out "overruled".
//
// So the vocabulary moves here, next to goodLawTier, for the same reason goodLawTier is here: two
// surfaces speaking about the same fact must read from one table or they drift, and this drift is
// user-visible on the first screen. LABEL is the citator's noun form; PHRASE is how the SAME fact is
// spoken inside a sentence. They are two renderings of one row, never two tables.
//
// `takesCase:false` means the treatment names no treating CASE — "superseded by statute" is done by a
// legislature, and appending "by <case name>" to it would invent a relation the record does not hold.
const TREATMENT_VOCAB = Object.freeze({
  overruled:          Object.freeze({ label: 'Overruled',             phrase: 'overruled by',          takesCase: true }),
  abrogated:          Object.freeze({ label: 'Abrogated',             phrase: 'abrogated by',          takesCase: true }),
  superseded_statute: Object.freeze({ label: 'Superseded by statute', phrase: 'superseded by statute', takesCase: false }),
  reversed_vacated:   Object.freeze({ label: 'Reversed',              phrase: 'reversed by',           takesCase: true }),
  // The engine's `top_treatment` (steel_goodlaw_flags) also emits these three bare forms. They were
  // present in Steel's server-side DEFINITIVE_NEG map but NOT in the citator's label map, so a case
  // carrying one of them rendered its raw column value in the verdict lede while the answer path
  // spoke it correctly — the same one-fact-two-vocabularies defect, in the other direction.
  superseded:         Object.freeze({ label: 'Superseded',            phrase: 'superseded by',         takesCase: true }),
  reversed:           Object.freeze({ label: 'Reversed',              phrase: 'reversed by',           takesCase: true }),
  vacated:            Object.freeze({ label: 'Vacated',               phrase: 'vacated by',            takesCase: true }),
  criticized:         Object.freeze({ label: 'Criticized',            phrase: 'criticized by',         takesCase: true }),
  questioned:         Object.freeze({ label: 'Questioned',            phrase: 'questioned by',         takesCase: true }),
  distinguished:      Object.freeze({ label: 'Distinguished',         phrase: 'distinguished by',      takesCase: true }),
  // Non-class values the resolver can also put in `treatment` — they name a VERDICT, not a relation,
  // so they have a label but no prose relation to speak.
  cache:              Object.freeze({ label: 'Not good law',          phrase: null,                    takesCase: false }),
  cheap_annotate:     Object.freeze({ label: 'Not good law',          phrase: null,                    takesCase: false }),
  landmark_good:      Object.freeze({ label: 'Good law',              phrase: null,                    takesCase: false }),
});

// The citator's noun form, keyed by treatment class. Kept as a plain map because Steel's client has
// imported it under this name since SESSION C; the map is now PROJECTED from the vocab table above
// rather than being a second hand-maintained copy of it.
export const TREATMENT_LABEL = Object.freeze(
  Object.fromEntries(Object.entries(TREATMENT_VOCAB).map(([k, v]) => [k, v.label])),
);

/**
 * Sentence-case the leading verb of a lede. Never uppercases past the first character:
 * 'superseded by statute' → 'Superseded by statute', never 'Superseded By Statute'; an
 * already-correct label passes through untouched. Applied at the LEDE SITES rather than at the map,
 * so a treatment class we have never seen still opens its sentence with a capital.
 */
export function leadVerb(word) {
  const s = String(word || '').replace(/_/g, ' ').trim();
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** The citator's label for a treatment class; an unmapped class is humanized, never dropped. */
export function treatmentLabel(treatment) {
  const key = String(treatment || '').toLowerCase();
  if (TREATMENT_VOCAB[key]) return TREATMENT_VOCAB[key].label;
  return key ? leadVerb(key) : null;
}

/**
 * ⭐ A4 — THE PERIOD-SAFE SENTENCE TERMINATOR.
 *
 * Live defect: "It was overruled by Kennedy v. Bremerton Sch. Dist.." — the abbreviation's own period
 * plus the sentence's. Legal case names END IN ABBREVIATIONS more often than not ("Sch. Dist.",
 * "Ass'n", "Inc.", "Co."), so a naive `${name}.` is wrong for a large share of real citations, not an
 * edge case. One period serves both the abbreviation and the sentence — that is the standard rule,
 * and it is the rule here.
 */
export function endSentence(text) {
  const s = String(text == null ? '' : text).trim();
  if (!s) return '';
  return /[.!?]$/.test(s) ? s : `${s}.`;
}

/**
 * The prose clause for a treatment, in the SAME vocabulary the citator card uses.
 *   treatmentClause('abrogated', 'Kennedy v. Bremerton School Dist.')
 *     → 'It was abrogated by Kennedy v. Bremerton School Dist.'   (one period, not two)
 *   treatmentClause('abrogated', null)      → 'It has been abrogated.'
 *   treatmentClause('superseded_statute')   → 'It was superseded by statute.'
 *   treatmentClause('nonsense')             → null   (caller falls back to its generic phrasing)
 *
 * Returns null rather than guessing whenever the record does not support a specific relation — the
 * caller's generic "It has received negative treatment." is the honest floor, and inventing a verb
 * the citation record never asserted is exactly the kind of confident-wrong sentence this product
 * exists to not produce.
 */
export function treatmentClause(treatment, treatingCaseName) {
  const key = String(treatment || '').toLowerCase();
  const v = TREATMENT_VOCAB[key];
  if (!v || !v.phrase) return null;
  const name = String(treatingCaseName || '').trim();
  if (!v.takesCase) return endSentence(`It was ${v.phrase}`);
  if (name) return endSentence(`It was ${v.phrase} ${name}`);
  // Known relation, unknown treating case: say the WORD from the record, drop the by-clause.
  return endSentence(`It has been ${v.phrase.replace(/\s+by$/i, '')}`);
}

/**
 * The FAITHFUL phrase for a treatment CLASS — for any surface that displays the class beside the
 * sentence it was read from.
 *
 * 🔴 WHY THIS IS NOT `TREATMENT_LABEL` / `treatmentClause`. Those speak the citator's CHIP
 * vocabulary, which deliberately collapses: `OVERRULED_IN_PART` → 'overruled',
 * `OVERRULED_ON_OTHER_GROUNDS` → 'questioned'. Fine on a chip. Wrong anywhere the reader can also
 * see the quotation, because then the paraphrase visibly claims more (or less) than the quote.
 * Caught live in Session H-2: Snyder v. Massachusetts rendered "cites this case as **overruled** by
 * 378 U.S. 1" directly above evidence reading "**overruled in part** by Malloy v. Hogan".
 *
 * SUPER BUNDLE LANE 4c — this map lived in `apps/steel/server/lib/goodlaw/precomputed.js` as
 * `CITED_PHRASE`, and the answer path needed the same words. A vocabulary table in this codebase was
 * already found in SIX copies, one of whose default branch upgraded an unknown treatment to
 * "overruled". Rather than write a seventh, the table moved here and both callers import it — so the
 * citator chip, the unconfirmed-tier sentence and the answer's currency caveat cannot drift apart.
 *
 * Returns null for an unrecognised class: the caller says the honest generic instead of inventing a
 * relation the record never asserted.
 */
const CLASS_PHRASE = Object.freeze({
  OVERRULED: 'overruled',
  OVERRULED_IN_PART: 'overruled in part',
  OVERRULED_ON_OTHER_GROUNDS: 'overruled on other grounds',
  ABROGATED: 'abrogated',
  SUPERSEDED_BY_STATUTE: 'superseded',
  REVERSED_ON_DIRECT_APPEAL: 'reversed on direct appeal',
  CRITICIZED: 'criticized',
  DISTINGUISHED: 'distinguished',
});
export function classPhrase(treatmentClass) {
  return CLASS_PHRASE[String(treatmentClass || '').toUpperCase()] || null;
}

/** The bare relation phrase ('abrogated by'), or null when the class names no relation. */
export function treatmentPhrase(treatment) {
  const v = TREATMENT_VOCAB[String(treatment || '').toLowerCase()];
  return v ? v.phrase : null;
}

/**
 * The full vocab row for a treatment class: { label, phrase, takesCase }, or null if unrecognised.
 * `takesCase` is the field callers MUST consult before appending a treating case name — 'superseded
 * by statute' is done by a legislature, and "superseded by statute Kennedy v. Bremerton" is a
 * relation the record never asserted.
 */
export function treatmentRelation(treatment) {
  return TREATMENT_VOCAB[String(treatment || '').toLowerCase()] || null;
}

// Convenience: full descriptor for a status (+ optional source). { tier, label, badLaw, neutral, verifiedGood }.
export function goodLawDescriptor(status, opts = {}) {
  const tier = goodLawTier(status, opts);
  return {
    tier,
    label: TIER_LABEL[tier],
    badLaw: tier === 'negative',
    neutral: tier === 'no-known-negative',
    verifiedGood: tier === 'verified-good',
  };
}

// ─── DOCUMENT-TYPE CLASSIFICATION (gates whether good-law applies) ───
// A MERITS OPINION has a good-law status; a NON-MERITS disposition (cert/writ/application denial, an order,
// an errata) does NOT — its good-law is 'N/A', never a fabricated status. This classifier is DETERMINISTIC
// (reads the corpus's precedential_status / disposition / citation strings — no LLM) and FAIL-SAFE: any doubt
// → MERITS. Mis-classifying a real opinion as procedural would BURY it AND skip its good-law check (a double
// failure), so non-merits is asserted ONLY on a clear order/denial signal; ambiguous ("Dismissed") → merits.
const _DENIAL_OF_REVIEW = /\b(cert(iorari)?\.?\s+denied|writ\s+denied|(discretionary|interlocutory)\s+application\s+denied|petition\s+(for\s+)?(a\s+)?(cert(iorari)?|review|writ|rehearing)\s+denied|leave\s+to\s+appeal\s+denied|(rehearing|reconsideration|review)\s+denied)\b/i;
const _NON_MERITS_STATUS = new Set(['errata', 'relating-to']); // corrections / orders relating to a case
const _CERT_DENIED_CITE = /\bcert\.?\s*denied\b/i;

// classifyDocType({ precedential_status, disposition, citations }) →
//   { merits:boolean, kind:'opinion'|'denial'|'cert-denied'|'order'|'errata', tag:string, precedential?:boolean, reason?:string }
export function classifyDocType({ precedential_status, disposition, citations } = {}) {
  const ps = String(precedential_status || '').toLowerCase();
  const disp = String(disposition || '');
  const citeStr = Array.isArray(citations)
    ? citations.map((c) => (typeof c === 'string' ? c : (c && (c.cite || c.citation || c.reporter)) || '')).join(' ')
    : String(citations || '');
  // NON-MERITS only on CLEAR signals (false-procedural-on-a-real-opinion must be ~0):
  if (_NON_MERITS_STATUS.has(ps)) return { merits: false, kind: ps === 'errata' ? 'errata' : 'order', tag: ps === 'errata' ? 'Errata' : 'Order', reason: `status=${precedential_status}` };
  if (_DENIAL_OF_REVIEW.test(disp)) return { merits: false, kind: 'denial', tag: /cert/i.test(disp) ? 'Cert denied' : 'Order', reason: 'disposition' };
  if (_CERT_DENIED_CITE.test(citeStr)) return { merits: false, kind: 'cert-denied', tag: 'Cert denied', reason: 'citation' };
  // MERITS (fail-safe default). Unpublished is still a merits opinion (check its good-law) — just tag it.
  const unpublished = ps === 'unpublished';
  return { merits: true, kind: 'opinion', tag: unpublished ? 'Unpublished' : 'Opinion', precedential: !unpublished };
}

// ─── CANONICAL JURISDICTION TREE (Indigo Book T1/T3) ───
// Re-exported so the research jurisdiction picker AND the A7 jurisdiction-honesty check share ONE source.
export { LEVEL, FEDERAL, STATES, TERRITORIES, allJurisdictions, COUNTS } from './jurisdictions.js';
// RUN JE — which QUESTION may be asked of which authority (the treatment allow-list + the category rule).
// Re-exported from the root so a client bundle needs no subpath import.
export * from './authorityKind.js';

export default {
  STATUS, isBadLaw, VERIFIED_SOURCES, isVerifiedSource, goodLawTier, TIER_LABEL, goodLawDescriptor, classifyDocType,
  // SESSION G+E A4 — the one treatment vocabulary (labels + prose + the period-safe join)
  TREATMENT_LABEL, leadVerb, treatmentLabel, treatmentClause, treatmentPhrase, treatmentRelation, endSentence,
  // LANE 4c — the FAITHFUL class phrase, for surfaces that show the class beside its quotation.
  classPhrase,
};
